import { AuthControllerService } from "@/api";
import { clearFilterState } from "@/components/Filters/state";
import {
  SESSION_EXPIRED,
  USER_LOCAL_STORAGE_KEY,
  ID_TOKEN_LOCAL_STORAGE_KEY,
  IS_DEMO_LOCAL_STORAGE_KEY,
  IS_AIMODE_LOCAL_STORAGE_KEY,
  TOKEN_EXPIRY_TIME_LOCAL_STORAGE_KEY,
  REFRESH_TOKEN_LOCAL_STORAGE_KEY,
} from "@/configs/site";
import { clearAgentSession } from "@/libs/agentSession";

export const setSessionExpired = (): void =>
  sessionStorage.setItem(SESSION_EXPIRED, "1");

// Read and clear are separate so a StrictMode double-run initializer still sees the flag.
export const isSessionExpired = (): boolean =>
  sessionStorage.getItem(SESSION_EXPIRED) === "1";

export const clearSessionExpired = (): void =>
  sessionStorage.removeItem(SESSION_EXPIRED);

/**
 * Returns true only when a token expiry time is stored and is in the past.
 * A missing or unparseable expiry returns false so legacy sessions keep
 * falling back to the 401 interceptor.
 */
export const isTokenExpired = (): boolean => {
  const tokenExpiryTime = localStorage.getItem(
    TOKEN_EXPIRY_TIME_LOCAL_STORAGE_KEY,
  );
  if (!tokenExpiryTime) {
    return false;
  }
  const expiry = new Date(tokenExpiryTime).getTime();
  return !Number.isNaN(expiry) && expiry <= Date.now();
};

/**
 * Clears all auth-related local storage without calling the logout API or
 * redirecting; used when the session is already invalid server-side.
 */
export const clearAuthStorage = (): void => {
  clearAgentSession();
  localStorage.removeItem(ID_TOKEN_LOCAL_STORAGE_KEY);
  localStorage.removeItem(USER_LOCAL_STORAGE_KEY);
  localStorage.removeItem(IS_DEMO_LOCAL_STORAGE_KEY);
  localStorage.removeItem(IS_AIMODE_LOCAL_STORAGE_KEY);
  localStorage.removeItem(TOKEN_EXPIRY_TIME_LOCAL_STORAGE_KEY);
  localStorage.removeItem(REFRESH_TOKEN_LOCAL_STORAGE_KEY);
  clearFilterState(); // Clear filter state on logout for all pages from the local storage
};

export const logOut = async () => {
  try {
    await AuthControllerService.userLogoutUsingPost();
  } catch (ex) {
    // eslint-disable-next-line no-console
    console.error(ex);
  }
  clearAuthStorage();
  window.location.href = "/login";
};

export const isDemoEnvironment = (): boolean => {
  let storageValue = localStorage.getItem(IS_DEMO_LOCAL_STORAGE_KEY) === "true";
  if (window.location.hostname.startsWith("demo")) {
    storageValue = true;
  }
  return storageValue;
};
